import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { galleryImages, homepageContent, siteContent } from '../../data/products';
import { EASE, staggerWrap } from '../../motion';
import './InstaStrip.css';

/* ---------------------------------------------------------------------------
 * Reels
 * ---------------------------------------------------------------------------
 * The account posts mostly reels, so every tile can carry an optional short
 * video that previews on hover. Keyed by image path rather than written as a
 * flat list because the tile images come from the CMS gallery (/admin) — a
 * hardcoded array would silently drift the day someone reorders the gallery.
 *
 * TO ATTACH A REEL:
 *   1. Drop the file in  public/videos/reels/   (muted, ~5-10s, vertical is fine)
 *   2. Add one line below, matching the tile's image path:
 *        '/images/kriams/latest-brownie-assortment.webp': '/videos/reels/brownie-box.mp4',
 * Tiles with no entry stay exactly as they are today — a still image.
 */
/* Coarse-pointer devices never fire hover, so there those tiles play when they
   scroll into view instead. Read once — pointer type doesn't change mid-visit. */
const isTapDevice = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: none)').matches;

/* Data Saver is an explicit "don't spend my bytes" — honour it by never
   mounting the <video> at all, not merely by declining to press play. */
const isSavingData = () => {
  const conn =
    typeof navigator !== 'undefined' &&
    (navigator.connection || navigator.mozConnection || navigator.webkitConnection);
  return Boolean(conn && conn.saveData);
};

/* Restrained on purpose — this sits two sections above the footer and must not
   read louder than the hero. Short travel, quick stagger, one pass only. */
const gridVariants = staggerWrap(0.06, 0.04);
const tileVariants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } }
};

const InstaTile = ({ tile, tapToPlay, allowVideo, instagramUrl }) => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [active, setActive] = useState(false); // wants to be playing
  const [ready, setReady] = useState(false); // canplay fired — safe to reveal
  const [failed, setFailed] = useState(false);

  const hasVideo = Boolean(tile.video) && allowVideo && !failed;
  // amount 0.6 so a tile only claims playback once it genuinely owns the screen
  const inView = useInView(ref, { amount: 0.6 });

  useEffect(() => {
    if (!hasVideo || !tapToPlay) return;
    setActive(inView);
  }, [inView, hasVideo, tapToPlay]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    if (active) {
      // React has historically dropped the `muted` attribute on hydration and
      // an unmuted play() is rejected outright — assert it before every play.
      video.muted = true;
      // play() also rejects with AbortError when a pause() lands while the
      // promise is still pending. A quick hover-out/hover-in leaves that stale
      // rejection in flight, and without this guard it would arrive *after*
      // the second play() and switch the now-playing video back off — with
      // the pointer still inside the tile, so no further hover event ever
      // comes to fix it. Ignore any rejection from a superseded attempt.
      let superseded = false;
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        // Autoplay can still be refused. Fall back to the poster rather than
        // leaving a frozen first frame sitting on top of the photo.
        attempt.catch(() => {
          if (!superseded) setActive(false);
        });
      }
      return () => {
        superseded = true;
      };
    }

    video.pause();
    // Rewind after the fade-out, not during it — frame 0 rarely matches the
    // poster shot, and the jump would be visible mid-crossfade.
    const rewind = setTimeout(() => {
      // readyState guard: seeking a video that has loaded nothing yet (the
      // common case, since preload="none" means it only fetches on first
      // hover) throws in Safari.
      if (video.readyState > 0) video.currentTime = 0;
    }, 260);
    return () => clearTimeout(rewind);
  }, [active]);

  const playing = hasVideo && active && ready;

  return (
    <motion.a
      ref={ref}
      className={`insta-tile${playing ? ' is-playing' : ''}`}
      href={instagramUrl}
      target="_blank"
      rel="noreferrer"
      aria-label={`${tile.label} — view on Instagram`}
      variants={tileVariants}
      whileHover={{ y: -4, scale: 1.015, transition: { duration: 0.25, ease: EASE } }}
      onHoverStart={() => !tapToPlay && setActive(true)}
      onHoverEnd={() => !tapToPlay && setActive(false)}
    >
      <img className="insta-img" src={tile.image} alt={tile.label} loading="lazy" />

      {hasVideo && (
        <video
          ref={videoRef}
          className="insta-video"
          src={tile.video}
          muted
          loop
          playsInline
          /* No poster: the <img> underneath already is one, and a poster
             attribute would download the same frame a second time. */
          preload="none"
          aria-hidden="true"
          onCanPlay={() => setReady(true)}
          onError={() => setFailed(true)}
        />
      )}

      {/* Badge stays even when the video is suppressed (reduced motion, data
          saver, load failure) — it describes the post, and the link still
          lands on the reel. */}
      {Boolean(tile.video) && (
        <span className="insta-reel-badge" aria-hidden="true">
          <svg viewBox="0 0 12 12" focusable="false">
            <path d="M4 2.6 9.2 6 4 9.4Z" />
          </svg>
        </span>
      )}

      <span className="insta-veil" aria-hidden="true">
        <span className="insta-handle">{siteContent.contact.instagramHandle}</span>
      </span>
    </motion.a>
  );
};

const InstaStrip = () => {
  const reduceMotion = useReducedMotion();
  const [tapToPlay, setTapToPlay] = useState(false);
  const [dataSaver, setDataSaver] = useState(false);
  const instagramUrl = siteContent.contact.instagramUrl;
  const instaTiles = galleryImages.slice(0, homepageContent.instagram.tileCount).map((img) => ({
    image: img.url,
    label: img.label,
    video: img.video
  }));

  // Read in an effect so the first paint is identical everywhere and the
  // production build never trips over navigator/matchMedia.
  useEffect(() => {
    setTapToPlay(isTapDevice());
    setDataSaver(isSavingData());
  }, []);

  const allowVideo = !reduceMotion && !dataSaver;

  if (!instaTiles.length) return null;

  return (
    <section id="insta-strip" className="insta-strip" aria-labelledby="insta-strip-title">
      <div className="insta-strip-inner">
        <header className="insta-strip-header">
          <span className="section-tag">{homepageContent.instagram.eyebrow}</span>
          <h2 id="insta-strip-title" className="section-title">{homepageContent.instagram.title}</h2>
        </header>

        <motion.div
          className="insta-grid"
          variants={gridVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {instaTiles.map((tile) => (
            <InstaTile
              key={tile.image}
              tile={tile}
              tapToPlay={tapToPlay}
              allowVideo={allowVideo}
              instagramUrl={instagramUrl}
            />
          ))}
        </motion.div>

        <div className="insta-strip-cta">
          <a
            className="insta-follow-btn"
            href={instagramUrl}
            target="_blank"
            rel="noreferrer"
          >
            {homepageContent.instagram.ctaLabel}
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstaStrip;
